import { FRDS_CHAT_ELEMENT_ID } from "@utils/constants";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGoftino } from "./useGoftino";

const useCheckFrdsGoftino = () => {
  const { hideGoftino } = useGoftino();
  const [isFrdsChat, setIsFrdsChat] = useState(false);
  const [showGoftinoIcon, setShowGoftinoIcon] = useState<boolean | undefined>();
  const [buttonOffset, setButtonOffset] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const widgetRef = useRef<HTMLElement | null>(null);

  const updateWidget = () => {
    const widget = widgetRef.current;
    if (!widget) return;

    const btn = widget.shadowRoot?.querySelector<HTMLButtonElement>("#btn");
    if (!btn) return;

    requestAnimationFrame(() => {
      const rect = btn.getBoundingClientRect();
      if (rect && (rect.height > 0 || rect.width > 0)) {
        setIsFrdsChat(true);
        hideGoftino();
      }
      if (showGoftinoIcon) {
        widget?.classList.remove("hidden");
        if (buttonOffset) {
          addButtonOffset(buttonOffset);
        }
      }
      if (!showGoftinoIcon && showGoftinoIcon !== undefined) {
        widget?.classList.add("hidden");
      }
    });
  };

  useEffect(() => {
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === "childList") {
          const widget = document.getElementById(FRDS_CHAT_ELEMENT_ID);
          widgetRef.current = widget;

          if (
            widget &&
            window.innerWidth <= 768 &&
            showGoftinoIcon === undefined
          ) {
            widget.classList.add("hidden");
          }

          timeoutRef.current = setTimeout(() => {
            updateWidget();
            observer.disconnect();
          }, 100);
        }
      }
    });

    const initialCheck = () => {
      const widget = document.getElementById(FRDS_CHAT_ELEMENT_ID);
      widgetRef.current = widget;
      timeoutRef.current = setTimeout(() => {
        updateWidget();
      }, 100);
    };

    initialCheck();
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      observer.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [showGoftinoIcon, buttonOffset]);

  const openChatFrds = useCallback(() => {
    const widget = widgetRef.current;
    if (!widget || !widget.shadowRoot) return;
    const btn = widget.shadowRoot.querySelector<HTMLButtonElement>("#btn");
    btn?.click();
  }, []);

  const addButtonOffset = (offset: number) => {
    const widget = widgetRef.current;
    if (!widget || !widget.shadowRoot) return;
    const btn = widget.shadowRoot.querySelector<HTMLButtonElement>("#btn");
    if (btn) {
      btn.style.setProperty("bottom", `${offset}px`, "important");
    }
  };

  const handleClickChat = useCallback(() => {
    if (window.Goftino && !isFrdsChat) window.Goftino.open();
    if (isFrdsChat) openChatFrds();
  }, [isFrdsChat, openChatFrds]);

  return {
    isFrdsChat,
    hideChatFrds: () => {
      setShowGoftinoIcon(false);
    },
    openChatFrds,
    showChatFrds: (offset: number = 0) => {
      setShowGoftinoIcon(true);
      setButtonOffset(offset);
    },
    initialChatFrds: () => {
      setShowGoftinoIcon(undefined);
      setButtonOffset(0);
    },
    handleClickChat,
  };
};

export default useCheckFrdsGoftino;
